import React, { useState } from "react";
import { Phone, MapPin, Globe } from "lucide-react";
import { SectionHeader } from "./Rates";
import { CONTACTS, INTEREST_OPTIONS, WHATSAPP_NUMBER } from "../mock";
import { useToast } from "../hooks/use-toast";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import api from "../lib/api";

const ICONS = { Phone, MapPin, Globe };

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", interest: "", message: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: "Please fill required fields", description: "Name and phone number are required." });
      return;
    }
    api.post("/enquiries", form).catch(() => {});
    const text = `Hi TwinBrain! %0AName: ${form.name}%0APhone: ${form.phone}%0AInterested in: ${form.interest || "General Enquiry"}%0AMessage: ${form.message || "-"}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, "_blank");
    toast({ title: "Enquiry sent", description: "Saved & opening WhatsApp to send." });
    setForm({ name: "", phone: "", interest: "", message: "" });
  };

  return (
    <section id="contact" className="relative py-24 bg-[#0b0f19]">
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <SectionHeader eyebrow="Get in Touch" title="Visit Us or Call" sub="Buy a coin, join the reward scheme, book via EMI, or explore business collaboration — we're here for you." />

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="grid sm:grid-cols-2 gap-5 content-start">
            {CONTACTS.map((c, i) => {
              const Icon = ICONS[c.icon];
              return (
                <div key={i} className="border border-white/8 bg-white/[0.02] p-6 card-glow flex items-start gap-4">
                  <div className="h-11 w-11 shrink-0 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold">
                    <Icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-display text-xl text-white break-all">{c.title}</h3>
                    <p className="font-body text-gray-400 text-sm mt-0.5">{c.sub}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] p-8">
            <h3 className="font-display smallcaps text-3xl text-white">Send an Enquiry</h3>
            <div className="mt-6 space-y-4">
              <Field label="Your Name"><input value={form.name} onChange={set("name")} className="tb-input" placeholder="Full name" /></Field>
              <Field label="Phone Number"><input value={form.phone} onChange={set("phone")} className="tb-input" placeholder="Mobile number" /></Field>
              <Field label="I'm interested in">
                <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                  <SelectTrigger className="tb-input h-auto"><SelectValue placeholder="Select an option" /></SelectTrigger>
                  <SelectContent className="bg-[#0b0f19] border-white/10 text-white">
                    {INTEREST_OPTIONS.map((o) => <SelectItem key={o} value={o} className="focus:bg-gold/15 focus:text-gold">{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Message (optional)"><textarea value={form.message} onChange={set("message")} rows={3} className="tb-input resize-none" placeholder="Your message" /></Field>
              <button type="submit" className="w-full gold-btn font-ui text-xs tracking-[0.2em] uppercase py-4 font-medium transition-all hover:shadow-lg hover:shadow-gold/20">Send Enquiry via WhatsApp</button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .tb-input {
          width: 100%;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.1);
          padding: 0.75rem 1rem;
          color: #fff;
          font-family: 'Poppins', sans-serif;
          outline: none;
          transition: border-color 0.2s;
        }
        .tb-input:focus { border-color: #d4af37; }
        .tb-input option { background: #0b0f19; }
      `}</style>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);

export default Contact;
